import React, { useEffect, useMemo, useState } from 'react';
import { useToastContext } from '../shared/context/ToastContext';
import { apiFetch } from '../shared/api';
import './AdminUsers.css';

function AdminUsers() {
  const { success, error: showError } = useToastContext();

  const [activeTab, setActiveTab] = useState('clients');
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [feedDomains, setFeedDomains] = useState([]);
  const [clientBrands, setClientBrands] = useState({});
  const [clientFeedDomains, setClientFeedDomains] = useState({});
  const [loading, setLoading] = useState(true);

  const [clientSearch, setClientSearch] = useState('');
  const [domainSearch, setDomainSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const [expandedClients, setExpandedClients] = useState({});
  const [expandedDomains, setExpandedDomains] = useState({});

  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateDomain, setShowCreateDomain] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);

  const [newClientName, setNewClientName] = useState('');
  const [feedDomainDraft, setFeedDomainDraft] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    role: 'user',
    client_id: ''
  });
  const [passwordDrafts, setPasswordDrafts] = useState({});
  const [brandDrafts, setBrandDrafts] = useState({});

  const fetchClientBrands = async (clientId) => {
    const response = await apiFetch(`/admin/clients/${clientId}/brands`);
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to load brands');
    }
    return response.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, clientsRes, feedDomainsRes] = await Promise.all([
        apiFetch('/admin/users'),
        apiFetch('/clients'),
        apiFetch('/admin/feed-domains')
      ]);
      if (!usersRes.ok) {
        const err = await usersRes.json();
        throw new Error(err.error || 'Failed to load users');
      }
      if (!clientsRes.ok) {
        const err = await clientsRes.json();
        throw new Error(err.error || 'Failed to load clients');
      }
      if (!feedDomainsRes.ok) {
        const err = await feedDomainsRes.json();
        throw new Error(err.error || 'Failed to load feed domains');
      }
      const usersData = await usersRes.json();
      const clientsData = await clientsRes.json();
      const feedDomainsData = await feedDomainsRes.json();
      setUsers(usersData);
      setClients(clientsData);
      setFeedDomains(feedDomainsData);
      const brandPairs = await Promise.all(
        clientsData.map(async (client) => {
          const brands = await fetchClientBrands(client.id);
          return [client.id, brands];
        })
      );
      setClientBrands(Object.fromEntries(brandPairs));
      const feedDomainPairs = await Promise.all(
        clientsData.map(async (client) => {
          const response = await apiFetch(`/admin/clients/${client.id}/feed-domains`);
          if (!response.ok) return [client.id, []];
          const assigned = await response.json();
          return [client.id, assigned.map((item) => item.id)];
        })
      );
      setClientFeedDomains(Object.fromEntries(feedDomainPairs));
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyToClipboard = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    success(`${label || 'ID'} copied`);
  };

  // Clients

  const createClient = async (e) => {
    e.preventDefault();
    const name = newClientName.trim();
    if (!name) return;
    try {
      const response = await apiFetch('/admin/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create client');
      success('Client created');
      setNewClientName('');
      setShowCreateClient(false);
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteClient = async (client) => {
    if (client.id === 'unassigned') {
      showError('Unassigned client cannot be deleted');
      return;
    }
    if (
      !window.confirm(
        `Delete client "${client.name}"? Campaigns and user assignments will be moved to Unassigned.`
      )
    ) {
      return;
    }
    try {
      const response = await apiFetch(`/admin/clients/${client.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete client');
      success('Client deleted');
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  // Brands

  const createBrand = async (clientId) => {
    const name = (brandDrafts[clientId] || '').trim();
    if (!name) {
      showError('Enter a brand name');
      return;
    }
    try {
      const response = await apiFetch(`/admin/clients/${clientId}/brands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create brand');
      success('Brand added');
      setBrandDrafts({ ...brandDrafts, [clientId]: '' });
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteBrand = async (clientId, brandId, brandName) => {
    if (!window.confirm(`Delete brand "${brandName}"?`)) return;
    try {
      const response = await apiFetch(`/admin/clients/${clientId}/brands/${brandId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete brand');
      success('Brand deleted');
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  // Feed Domains

  const createFeedDomain = async (e) => {
    e.preventDefault();
    const host = feedDomainDraft.trim().toLowerCase();
    if (!host) return;
    try {
      const response = await apiFetch('/admin/feed-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host, is_active: 1 })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create feed domain');
      success('Feed domain created');
      setFeedDomainDraft('');
      setShowCreateDomain(false);
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteFeedDomain = async (domain) => {
    if (!window.confirm(`Delete feed domain "${domain.host}"?`)) return;
    try {
      const response = await apiFetch(`/admin/feed-domains/${domain.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete feed domain');
      success('Feed domain deleted');
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  // Auto-save assignments

  const persistClientFeedDomains = async (clientId, nextIds, prevIds) => {
    try {
      const response = await apiFetch(`/admin/clients/${clientId}/feed-domains`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feed_domain_ids: nextIds })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save feed domains');
      setClientFeedDomains((prev) => ({
        ...prev,
        [clientId]: data.map((item) => item.id)
      }));
    } catch (err) {
      showError(err.message);
      setClientFeedDomains((prev) => ({ ...prev, [clientId]: prevIds }));
    }
  };

  const toggleClientFeedDomain = (clientId, domainId) => {
    const prevIds = clientFeedDomains[clientId] || [];
    const next = new Set(prevIds);
    if (next.has(domainId)) next.delete(domainId);
    else next.add(domainId);
    const nextIds = Array.from(next);
    setClientFeedDomains((prev) => ({ ...prev, [clientId]: nextIds }));
    persistClientFeedDomains(clientId, nextIds, prevIds);
  };

  const toggleDomainClientAssignment = (domainId, clientId) => {
    const prevIds = clientFeedDomains[clientId] || [];
    const next = new Set(prevIds);
    if (next.has(domainId)) next.delete(domainId);
    else next.add(domainId);
    const nextIds = Array.from(next);
    setClientFeedDomains((prev) => ({ ...prev, [clientId]: nextIds }));
    persistClientFeedDomains(clientId, nextIds, prevIds);
  };

  // Users

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: newUser.email.trim(),
        password: newUser.password,
        role: newUser.role,
        client_id: newUser.role === 'admin' ? null : (newUser.client_id || null)
      };
      const response = await apiFetch('/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create user');
      success('User created');
      setNewUser({ email: '', password: '', role: 'user', client_id: '' });
      setShowCreateUser(false);
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  const updateUser = async (user, patch) => {
    try {
      const body = {};
      if (patch.role !== undefined) body.role = patch.role;
      if (patch.is_active !== undefined) body.is_active = patch.is_active;
      if (patch.client_id !== undefined) body.client_id = patch.client_id;
      else if (patch.role !== undefined && patch.role !== 'admin') {
        body.client_id = user.primary_client_id ?? null;
      }
      if (typeof patch.password === 'string' && patch.password.trim()) {
        body.password = patch.password.trim();
      }

      const response = await apiFetch(`/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update user');
      success('User updated');
      await loadData();
    } catch (err) {
      showError(err.message);
    }
  };

  // Filtered lists

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((client) => client.name.toLowerCase().includes(q));
  }, [clients, clientSearch]);

  const filteredDomains = useMemo(() => {
    const q = domainSearch.trim().toLowerCase();
    if (!q) return feedDomains;
    return feedDomains.filter((domain) => domain.host.toLowerCase().includes(q));
  }, [feedDomains, domainSearch]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      user.email.toLowerCase().includes(q) ||
      (user.primary_client_name || '').toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const clientCountForDomain = (domainId) =>
    clients.filter((client) =>
      (clientFeedDomains[client.id] || []).includes(domainId)
    ).length;

  if (loading) return <div className="loading">Loading admin...</div>;

  return (
    <div className="admin-users">
      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`admin-tab ${activeTab === 'clients' ? 'is-active' : ''}`}
          aria-selected={activeTab === 'clients'}
          onClick={() => setActiveTab('clients')}
        >
          Clients <span className="admin-tab-count">{clients.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          className={`admin-tab ${activeTab === 'domains' ? 'is-active' : ''}`}
          aria-selected={activeTab === 'domains'}
          onClick={() => setActiveTab('domains')}
        >
          Feed Domains <span className="admin-tab-count">{feedDomains.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          className={`admin-tab ${activeTab === 'users' ? 'is-active' : ''}`}
          aria-selected={activeTab === 'users'}
          onClick={() => setActiveTab('users')}
        >
          Users <span className="admin-tab-count">{users.length}</span>
        </button>
      </div>

      {activeTab === 'clients' && (
        <div className="card">
          <div className="admin-tab-header">
            <input
              className="admin-search"
              type="text"
              placeholder="Search clients..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowCreateClient((v) => !v)}
            >
              {showCreateClient ? 'Cancel' : '+ Add Client'}
            </button>
          </div>

          {showCreateClient && (
            <form onSubmit={createClient} className="admin-inline-create">
              <input
                type="text"
                placeholder="Client name"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                autoFocus
                required
              />
              <button type="submit" className="btn btn-primary btn-sm">Create</button>
            </form>
          )}

          <div className="admin-row-list">
            {filteredClients.map((client) => {
              const isExpanded = !!expandedClients[client.id];
              const brands = clientBrands[client.id] || [];
              const domains = (clientFeedDomains[client.id] || []).length;
              return (
                <div key={client.id} className="admin-row">
                  <button
                    type="button"
                    className="admin-row-summary"
                    onClick={() =>
                      setExpandedClients({
                        ...expandedClients,
                        [client.id]: !isExpanded
                      })
                    }
                  >
                    <span className={`admin-row-chevron ${isExpanded ? 'is-open' : ''}`}>▸</span>
                    <div className="admin-row-title">
                      <strong>{client.name}</strong>
                      <span className="admin-row-meta">
                        {brands.length} {brands.length === 1 ? 'brand' : 'brands'} · {domains} {domains === 1 ? 'domain' : 'domains'}
                      </span>
                    </div>
                  </button>
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="admin-icon-btn"
                      onClick={() => copyToClipboard(client.id, 'Client ID')}
                      title="Copy client ID"
                    >
                      ID
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteClient(client)}
                      disabled={client.id === 'unassigned'}
                    >
                      Delete
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="admin-row-body">
                      <div className="admin-subsection">
                        <div className="admin-subsection-header">
                          <span className="admin-subsection-title">Brands</span>
                        </div>
                        <div className="admin-brand-list">
                          {brands.map((brand) => (
                            <span key={brand.id} className="admin-brand-pill">
                              {brand.name}
                              <button
                                type="button"
                                className="admin-brand-delete"
                                onClick={() => deleteBrand(client.id, brand.id, brand.name)}
                                aria-label={`Delete ${brand.name}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {brands.length === 0 && (
                            <span className="admin-subtle-text">No brands assigned</span>
                          )}
                        </div>
                        <div className="admin-inline-create admin-inline-create-tight">
                          <input
                            type="text"
                            placeholder="Add brand"
                            value={brandDrafts[client.id] || ''}
                            onChange={(e) =>
                              setBrandDrafts({ ...brandDrafts, [client.id]: e.target.value })
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                createBrand(client.id);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => createBrand(client.id)}
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      <div className="admin-subsection">
                        <div className="admin-subsection-header">
                          <span className="admin-subsection-title">Allowed feed domains</span>
                          <span className="admin-subtle-text">Auto-saves on toggle</span>
                        </div>
                        {feedDomains.length === 0 ? (
                          <span className="admin-subtle-text">No feed domains created yet</span>
                        ) : (
                          <div className="admin-domain-list">
                            {feedDomains.map((domain) => {
                              const checked = (clientFeedDomains[client.id] || []).includes(domain.id);
                              return (
                                <label
                                  key={`${client.id}-${domain.id}`}
                                  className="admin-domain-option"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleClientFeedDomain(client.id, domain.id)}
                                  />
                                  <span>{domain.host}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredClients.length === 0 && (
              <p className="admin-empty">
                {clientSearch ? 'No clients match your search.' : 'No clients yet.'}
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'domains' && (
        <div className="card">
          <div className="admin-tab-header">
            <input
              className="admin-search"
              type="text"
              placeholder="Search feed domains..."
              value={domainSearch}
              onChange={(e) => setDomainSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowCreateDomain((v) => !v)}
            >
              {showCreateDomain ? 'Cancel' : '+ Add Feed Domain'}
            </button>
          </div>

          {showCreateDomain && (
            <form onSubmit={createFeedDomain} className="admin-inline-create">
              <input
                type="text"
                placeholder="xml.example.com"
                value={feedDomainDraft}
                onChange={(e) => setFeedDomainDraft(e.target.value)}
                autoFocus
              />
              <button type="submit" className="btn btn-primary btn-sm">Create</button>
            </form>
          )}

          <div className="admin-row-list">
            {filteredDomains.map((domain) => {
              const isExpanded = !!expandedDomains[domain.id];
              const count = clientCountForDomain(domain.id);
              return (
                <div key={domain.id} className="admin-row">
                  <button
                    type="button"
                    className="admin-row-summary"
                    onClick={() =>
                      setExpandedDomains({
                        ...expandedDomains,
                        [domain.id]: !isExpanded
                      })
                    }
                  >
                    <span className={`admin-row-chevron ${isExpanded ? 'is-open' : ''}`}>▸</span>
                    <div className="admin-row-title">
                      <strong>{domain.host}</strong>
                      <span className="admin-row-meta">
                        {count} {count === 1 ? 'client' : 'clients'} assigned
                      </span>
                    </div>
                  </button>
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="admin-icon-btn"
                      onClick={() => copyToClipboard(domain.host, 'Host')}
                      title="Copy host"
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => deleteFeedDomain(domain)}
                    >
                      Delete
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="admin-row-body">
                      <div className="admin-subsection">
                        <div className="admin-subsection-header">
                          <span className="admin-subsection-title">Assign clients to this domain</span>
                          <span className="admin-subtle-text">Auto-saves on toggle</span>
                        </div>
                        <div className="admin-domain-list">
                          {clients.map((client) => {
                            const checked = (clientFeedDomains[client.id] || []).includes(domain.id);
                            return (
                              <label
                                key={`${domain.id}-${client.id}`}
                                className="admin-domain-option"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    toggleDomainClientAssignment(domain.id, client.id)
                                  }
                                />
                                <span>{client.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredDomains.length === 0 && (
              <p className="admin-empty">
                {domainSearch ? 'No domains match your search.' : 'No feed domains yet.'}
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card">
          <div className="admin-tab-header">
            <input
              className="admin-search"
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowCreateUser((v) => !v)}
            >
              {showCreateUser ? 'Cancel' : '+ Add User'}
            </button>
          </div>

          {showCreateUser && (
            <form onSubmit={createUser} className="admin-inline-create-block">
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Temporary Password</label>
                  <input
                    type="text"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Client</label>
                  <select
                    value={newUser.client_id}
                    onChange={(e) => setNewUser({ ...newUser, client_id: e.target.value })}
                    disabled={newUser.role === 'admin'}
                  >
                    <option value="">Select client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button className="btn btn-primary btn-sm" type="submit">Create User</button>
            </form>
          )}

          <div className="admin-row-list">
            {filteredUsers.map((user) => (
              <div key={user.id} className="admin-row admin-row-user">
                <div className="admin-row-summary admin-row-summary-static">
                  <div className="admin-row-title">
                    <strong>{user.email}</strong>
                    <span className="admin-row-meta">
                      {user.primary_client_name || user.primary_client_id || 'no client'} · {user.role} · {user.is_active ? 'active' : 'disabled'}
                    </span>
                  </div>
                </div>
                <div className="admin-row-actions admin-row-actions-wrap">
                  <input
                    className="admin-password-input"
                    type="password"
                    autoComplete="new-password"
                    placeholder="New password"
                    value={passwordDrafts[user.id] || ''}
                    onChange={(e) =>
                      setPasswordDrafts({ ...passwordDrafts, [user.id]: e.target.value })
                    }
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      const newPassword = (passwordDrafts[user.id] || '').trim();
                      if (!newPassword) {
                        showError('Enter a password first');
                        return;
                      }
                      updateUser(user, { password: newPassword });
                      setPasswordDrafts({ ...passwordDrafts, [user.id]: '' });
                    }}
                  >
                    Reset Password
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      updateUser(user, { role: user.role === 'admin' ? 'user' : 'admin' })
                    }
                  >
                    Make {user.role === 'admin' ? 'User' : 'Admin'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => updateUser(user, { is_active: user.is_active ? 0 : 1 })}
                  >
                    {user.is_active ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <p className="admin-empty">
                {userSearch ? 'No users match your search.' : 'No users yet.'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
